/**
 * Quote Detail Page.
 *
 * Document-style quote view with line items, trade-in credits,
 * financing options, version history, and actions (send, revise,
 * accept, convert to SO).
 */

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Copy,
  DollarSign,
  Plus,
  Receipt,
  RefreshCw,
  Send,
  Trash2,
  X,
  Mail,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useQuote } from "@/features/crm/api";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import DocumentHeader from "@/components/ui/DocumentHeader";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import ComposeEmail from "@/components/ui/ComposeEmail";
import TaxDrawer from "@/components/ui/TaxDrawer";
import { splitChargeRows } from "@/utils/taxDisplay";

const statusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  draft: "default",
  sent: "info",
  viewed: "info",
  accepted: "success",
  declined: "danger",
  expired: "warning",
  revised: "default",
};

function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: quote, isLoading } = useQuote(id);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.put(`/api/v1/quotes/${id}`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes", id] });
      toast.success("Quote updated");
    },
    onError: () => toast.error("Failed to update quote"),
  });

  const reviseMutation = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/api/v1/quotes/${id}/revise`)).data,
    onSuccess: (data: Record<string, unknown>) => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      navigate(`/quotes/${data.id}`);
      toast.success("New version created");
    },
    onError: () => toast.error("Failed to revise quote"),
  });

  const convertMutation = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/api/v1/quotes/${id}/convert`)).data,
    onSuccess: (data: Record<string, unknown>) => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
      navigate(`/sales/${data.id}`);
      toast.success("Sales order created from quote");
    },
    onError: () => toast.error("Failed to convert quote"),
  });

  const addLineMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post(`/api/v1/quotes/${id}/lines`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes", id] });
      toast.success("Line added");
    },
    onError: () => toast.error("Failed to add line"),
  });

  const removeLineMutation = useMutation({
    mutationFn: async (lineId: string) => {
      await apiClient.delete(`/api/v1/quotes/${id}/lines/${lineId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes", id] });
      toast.success("Line removed");
    },
    onError: () => toast.error("Failed to remove line"),
  });

  /**
   * Recalculate a quote's taxes and fees.
   *
   * The only tax action a quote has — there is no override route, which is why
   * the editable panel that used to live on this page could not work.
   */
  const applyTaxesMutation = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/api/v1/quotes/${id}/apply-taxes`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotes", id] }),
  });

  const [showEmail, setShowEmail] = useState(false);
  const [showTaxDrawer, setShowTaxDrawer] = useState(false);
  const [showAddLine, setShowAddLine] = useState(false);
  const [lineDesc, setLineDesc] = useState("");
  const [lineQty, setLineQty] = useState("1");
  const [linePrice, setLinePrice] = useState("");

  const fmt = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  if (isLoading) return <LoadingSpinner className="py-24" size="lg" />;
  if (!quote)
    return (
      <div className="py-24 text-center">
        <p className="text-[var(--text-secondary)]">Quote not found.</p>
      </div>
    );

  const isDraft = quote.status === "draft" || quote.status === "sent";

  // A quote's fee rows are order-level by construction: it has no line-charge
  // surface, so the engine files even a line-level scheme's money here.
  const { fee: feeRows, feeTotal: feesTotal } = splitChargeRows(
    quote.tax_lines || [],
  );
  const taxableFees = feeRows.reduce(
    (sum, r) => sum + (r.is_taxable ? Number(r.applied_tax) : 0),
    0,
  );

  const handleAddLine = async (e: React.FormEvent) => {
    e.preventDefault();
    await addLineMutation.mutateAsync({
      description: lineDesc,
      quantity: parseInt(lineQty),
      unit_price: parseFloat(linePrice),
    });
    setShowAddLine(false);
    setLineDesc("");
    setLineQty("1");
    setLinePrice("");
  };

  return (
    <div className="page-content space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate(-1)}
            className="mt-1 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                {quote.quote_number}
              </h2>
              <Badge variant={statusVariant[quote.status] || "default"}>
                {quote.status}
              </Badge>
              <span className="text-sm text-[var(--text-tertiary)]">
                v{quote.version}
              </span>
            </div>
            {quote.valid_until && (
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Valid until {new Date(quote.valid_until).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {quote.status === "draft" && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => updateMutation.mutate({ status: "sent" })}
            >
              <Send className="h-3.5 w-3.5" /> Mark Sent
            </Button>
          )}
          {(quote.status === "sent" || quote.status === "viewed") && (
            <>
              <Button
                size="sm"
                onClick={() => updateMutation.mutate({ status: "accepted" })}
              >
                <Check className="h-3.5 w-3.5" /> Accept
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => updateMutation.mutate({ status: "declined" })}
              >
                <X className="h-3.5 w-3.5" /> Decline
              </Button>
            </>
          )}
          {quote.status === "accepted" && !quote.converted_to_so_id && (
            <Button
              size="sm"
              onClick={() => convertMutation.mutate()}
              isLoading={convertMutation.isPending}
            >
              <DollarSign className="h-3.5 w-3.5" /> Convert to Sales Order
            </Button>
          )}
          {isDraft && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => reviseMutation.mutate()}
              isLoading={reviseMutation.isPending}
            >
              <RefreshCw className="h-3.5 w-3.5" /> New Version
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowEmail(true)}
          >
            <Mail className="h-3.5 w-3.5" /> Email Quote
          </Button>
          {quote.share_token && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                navigator.clipboard.writeText(
                  `${window.location.origin}/p/quote/${quote.share_token}`,
                );
                toast.success("Link copied");
              }}
            >
              <Copy className="h-3.5 w-3.5" /> Copy Link
            </Button>
          )}
        </div>
      </div>

      <DocumentHeader
        documentType="Quote"
        documentNumber={`${quote.quote_number} v${quote.version}`}
        status={quote.status}
        statusVariant={statusVariant[quote.status] || "default"}
        date={quote.created_at}
        dueDate={quote.valid_until}
        dueDateLabel="Valid Until"
        party={quote.customer_name ? { name: quote.customer_name } : undefined}
        extra={{
          "Payment Terms": quote.payment_terms || undefined,
          "Delivery Terms": quote.delivery_terms || undefined,
        }}
      />

      {/* Financial Summary */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-6">
        {[
          ["Subtotal", quote.subtotal],
          // A quote's fees are in its Total, so they need a card of their own —
          // without one the cards visibly fail to add up. Quotes have no
          // line-charge surface, so every fee is an order-level row.
          ...(feesTotal > 0 ? [["Fees", feesTotal] as const] : []),
          ["Tax", quote.tax],
          ["Shipping", quote.shipping],
          ["Trade-in", -quote.trade_in_credit],
          ["Discount", -quote.discount],
          ["Total", quote.total],
        ].map(([label, val]) => (
          <Card key={label as string} padding="sm">
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
              {label as string}
            </p>
            <p
              className={`mt-1 text-lg font-bold ${label === "Total" ? "text-brand-600" : (val as number) < 0 ? "text-[var(--text-success)]" : "text-[var(--text-primary)]"}`}
            >
              {(val as number) < 0
                ? `(${fmt(Math.abs(val as number))})`
                : fmt(val as number)}
            </p>
          </Card>
        ))}
      </div>

      {/* Tax — read-only breakdown plus the one action a quote actually has.
          Quotes have no tax-override route, so the editable panel that used to
          sit here offered a Save button that could only ever 404. */}
      <Card padding="sm">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setShowTaxDrawer(true)}
            className="group flex items-center gap-1.5 text-sm text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
          >
            <Receipt className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
            Tax breakdown
            <span className="text-[var(--text-tertiary)]">
              ({(quote.tax_lines || []).length}{" "}
              {(quote.tax_lines || []).length === 1 ? "line" : "lines"})
            </span>
          </button>
          {isDraft && (
            <Button
              size="sm"
              variant="secondary"
              isLoading={applyTaxesMutation.isPending}
              onClick={() =>
                applyTaxesMutation.mutate(undefined, {
                  onSuccess: () => toast.success("Taxes recalculated"),
                  onError: () => toast.error("Failed to recalculate taxes"),
                })
              }
            >
              Recalculate
            </Button>
          )}
        </div>
      </Card>

      <TaxDrawer
        isOpen={showTaxDrawer}
        onClose={() => setShowTaxDrawer(false)}
        entityType="quote"
        entityId={quote.id}
        taxLines={quote.tax_lines || []}
        basis={{
          subtotal: Number(quote.subtotal),
          discountAmount: Number(quote.discount),
          taxableCharges: taxableFees,
        }}
        taxTotal={Number(quote.tax)}
        // Read-only: a quote has apply-taxes and nothing else. The Recalculate
        // button above is the whole editing surface.
        canEdit={false}
        queryKey={["quotes", id!]}
      />

      {/* Line Items */}
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-[var(--border-default)] px-4 py-3">
          <h3 className="text-sm font-medium text-[var(--text-primary)]">
            Line Items ({quote.line_items.length})
          </h3>
          {isDraft && (
            <Button size="sm" onClick={() => setShowAddLine(!showAddLine)}>
              {showAddLine ? (
                "Cancel"
              ) : (
                <>
                  <Plus className="h-4 w-4" /> Add Item
                </>
              )}
            </Button>
          )}
        </div>

        {showAddLine && (
          <form
            onSubmit={handleAddLine}
            className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)] p-4"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
              <div className="sm:col-span-2">
                <Input
                  label="Description"
                  value={lineDesc}
                  onChange={(e) => setLineDesc(e.target.value)}
                  required
                  placeholder="Equipment or service description"
                />
              </div>
              <Input
                label="Quantity"
                type="number"
                min="1"
                value={lineQty}
                onChange={(e) => setLineQty(e.target.value)}
                required
              />
              <Input
                label="Unit Price"
                type="number"
                step="0.01"
                min="0"
                value={linePrice}
                onChange={(e) => setLinePrice(e.target.value)}
                required
                placeholder="0.00"
              />
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                type="submit"
                size="sm"
                isLoading={addLineMutation.isPending}
              >
                Add
              </Button>
            </div>
          </form>
        )}

        {quote.line_items.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2 text-right">Qty</th>
                <th className="px-4 py-2 text-right">Unit Price</th>
                <th className="px-4 py-2 text-right">Total</th>
                {isDraft && <th className="px-4 py-2 w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {quote.line_items.map((line) => (
                <tr
                  key={line.id}
                  className="border-b border-[var(--border-subtle)]"
                >
                  <td className="px-4 py-2 text-[var(--text-primary)]">
                    {line.description}
                  </td>
                  <td className="px-4 py-2 text-right">{line.quantity}</td>
                  <td className="px-4 py-2 text-right">
                    {fmt(line.unit_price)}
                  </td>
                  <td className="px-4 py-2 text-right font-medium">
                    {fmt(line.line_total)}
                  </td>
                  {isDraft && (
                    <td className="px-4 py-2">
                      <button
                        onClick={() => removeLineMutation.mutate(line.id)}
                        className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
            No line items. Add equipment or services above.
          </p>
        )}
      </Card>

      {/* Financing Options */}
      {quote.financing_options && quote.financing_options.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">
            Financing Options
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {quote.financing_options.map(
              (opt: Record<string, unknown>, i: number) => (
                <Card
                  key={i}
                  padding="sm"
                  className="bg-[var(--surface-sunken)]"
                >
                  <p className="text-xs text-[var(--text-tertiary)]">
                    Option {i + 1}
                  </p>
                  <div className="mt-1 space-y-1 text-sm">
                    {opt.rate != null && (
                      <p>
                        Rate: <strong>{Number(opt.rate)}%</strong>
                      </p>
                    )}
                    {opt.term_months != null && (
                      <p>
                        Term: <strong>{Number(opt.term_months)} months</strong>
                      </p>
                    )}
                    {opt.monthly_payment != null && (
                      <p>
                        Monthly:{" "}
                        <strong>{fmt(Number(opt.monthly_payment))}</strong>
                      </p>
                    )}
                    {opt.down_payment != null && (
                      <p>
                        Down: <strong>{fmt(Number(opt.down_payment))}</strong>
                      </p>
                    )}
                  </div>
                </Card>
              ),
            )}
          </div>
        </Card>
      )}

      {/* Notes */}
      {(quote.notes || quote.internal_notes) && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {quote.notes && (
            <Card>
              <h3 className="text-xs font-semibold text-[var(--text-tertiary)] uppercase mb-2">
                Customer Notes
              </h3>
              <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap">
                {quote.notes}
              </p>
            </Card>
          )}
          {quote.internal_notes && (
            <Card>
              <h3 className="text-xs font-semibold text-[var(--text-tertiary)] uppercase mb-2">
                Internal Notes
              </h3>
              <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap">
                {quote.internal_notes}
              </p>
            </Card>
          )}
        </div>
      )}

      <ComposeEmail
        isOpen={showEmail}
        onClose={() => setShowEmail(false)}
        defaultSubject={"Quote " + quote.quote_number}
        entityType="quote"
        entityId={quote.id}
      />
    </div>
  );
}

export default QuoteDetailPage;
